import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { User } from "@/shared/types";
import { GameState } from "@/shared/constants/game";
import { useAppSocketContext, useAuth } from "@/shared/hooks";
import { fetchPoolTournamentState } from "@/shared/api";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectPoolRoomState } from "@/store/slices/poolSlice";
import {
  poolTournamentStateHydrated,
  selectPoolTournamentError,
  selectPoolTournamentState
} from "@/store/slices/poolTournamentSlice";
import {
  PoolSessionState,
  poolSessionJoined,
  poolTournamentLifecycleUpdated,
  selectPoolSessionActiveMatchRoomId,
  selectPoolSessionError,
  selectPoolSessionState,
  setPoolSessionUserId
} from "@/store/slices/poolSessionSlice";
import {
  hideModal,
  selectShowPoolTournamentCancelledModal,
  selectShowPoolTournamentOverModal
} from "@/store/slices/uiSlice";
import PoolGame from "../game/PoolGame";
import "@/css/pool.css";
import "@/css/inGame.css";

function PoolGameDashboard({ user, gameState }: { user: User | null; gameState: GameState }) {
  void gameState;
  const { sessionID, roomID } = useParams<{ sessionID?: string; roomID?: string }>();
  const { lobbyUpdateData, setLobbyUpdateData } = useAppSocketContext();
  const { firebaseUser } = useAuth();
  const dispatch = useAppDispatch();
  const tournamentState = useAppSelector(selectPoolTournamentState);
  const roomState = useAppSelector(selectPoolRoomState);
  const tournamentError = useAppSelector(selectPoolTournamentError);
  const poolSessionState = useAppSelector(selectPoolSessionState);
  const poolSessionError = useAppSelector(selectPoolSessionError);
  const activeMatchRoomId = useAppSelector(selectPoolSessionActiveMatchRoomId);
  const showPoolTournamentOverModal = useAppSelector(selectShowPoolTournamentOverModal);
  const showPoolTournamentCancelledModal = useAppSelector(selectShowPoolTournamentCancelledModal);
  const [releaseTime, setReleaseTime] = useState<string | null>(null);
  const [lobbyUsers, setLobbyUsers] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [clockNow, setClockNow] = useState<number>(Date.now());

  useEffect(() => {
    dispatch(setPoolSessionUserId(user?.id ?? null));
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (!firebaseUser || !sessionID) {
      return;
    }

    const fetchState = async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const next = await fetchPoolTournamentState(sessionID, idToken);
        dispatch(poolTournamentStateHydrated(next));
        dispatch(poolSessionJoined(next.tournamentId));
        dispatch(poolTournamentLifecycleUpdated(next));
      } catch (error) {
        console.error("Failed to fetch pool tournament", error);
      }
    };

    fetchState();
  }, [dispatch, firebaseUser, sessionID]);

  useEffect(() => {
    if (!sessionID) {
      return;
    }

    const fetchLobby = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getLobby?tournamentId=${sessionID}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        });
        if (!response.ok) {
          throw new Error("Failed to fetch lobby");
        }

        const data = await response.json();
        setLobbyUsers(Array.isArray(data.usernames) ? data.usernames : []);
        setReleaseTime(data.releaseTime || null);
      } catch (error) {
        console.error("Failed to fetch pool lobby", error);
      }
    };

    fetchLobby();
  }, [sessionID]);

  useEffect(() => {
    if (!sessionID || !lobbyUpdateData || lobbyUpdateData.tournamentId !== Number(sessionID)) {
      return;
    }

    setLobbyUsers(lobbyUpdateData.usernames || []);
    setReleaseTime(lobbyUpdateData.releaseTime || null);
    setLobbyUpdateData(null);
  }, [lobbyUpdateData, sessionID, setLobbyUpdateData]);

  useEffect(() => {
    if (!releaseTime) {
      setTimeLeft("");
      return;
    }

    const interval = setInterval(() => {
      const diff = new Date(releaseTime).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft("Live");
        return;
      }

      const hours = String(Math.floor(diff / (1000 * 60 * 60))).padStart(2, "0");
      const minutes = String(Math.floor((diff / (1000 * 60)) % 60)).padStart(2, "0");
      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, "0");
      setTimeLeft(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [releaseTime]);

  useEffect(() => {
    if (!roomState || roomState.status !== "active") {
      return;
    }
    const interval = window.setInterval(() => setClockNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, [roomState?.status, roomState?.roomId]);

  const participantMap = useMemo(() => {
    const map = new Map<number, string>();
    tournamentState?.participants.forEach((participant) => map.set(participant.userId, participant.username));
    return map;
  }, [tournamentState]);

  const myActiveMatch = useMemo(() => {
    if (!user || !tournamentState) {
      return null;
    }
    return (
      tournamentState.matches.find(
        (match) =>
          match.round === tournamentState.round &&
          match.status === "active" &&
          (match.playerAUserId === user.id || match.playerBUserId === user.id)
      ) || null
    );
  }, [tournamentState, user]);

  const participantCount = lobbyUsers.length || tournamentState?.participants.length || 0;
  const isSessionRoute = Boolean(sessionID) && !roomID;
  const myRack = roomState?.rackStates?.find((r) => r.userId === user?.id);
  const opponentRack = roomState?.rackStates?.find((r) => r.userId !== user?.id);
  const myName =
    roomState?.players.find((player) => player.userId === user?.id)?.username || user?.username || "You";
  const opponentName =
    roomState?.players.find((player) => player.userId === opponentRack?.userId)?.username || "Opponent";
  const currentTurnUserID = roomState?.currentTurnUserId;
  const turnDurationMs = roomState?.turnDurationMs && roomState.turnDurationMs > 0 ? roomState.turnDurationMs : 60_000;
  const turnRemainingMs =
    roomState?.status === "active" &&
    roomState?.phase === "aiming" &&
    (roomState?.turnDeadlineTs || 0) > 0
      ? Math.max(0, (roomState?.turnDeadlineTs || 0) - clockNow)
      : 0;
  const turnRemainingPct = Math.max(0, Math.min(100, Math.round((turnRemainingMs / turnDurationMs) * 100)));
  const formatTimer = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };
  const poolBallColor = (number: number): string => {
    if (number === 8) return "#111111";
    if (number === 1 || number === 9) return "#f4c542";
    if (number === 2 || number === 10) return "#2449d8";
    if (number === 3 || number === 11) return "#e24b39";
    if (number === 4 || number === 12) return "#7b2ab8";
    if (number === 5 || number === 13) return "#ff8c1a";
    if (number === 6 || number === 14) return "#239a4b";
    if (number === 7 || number === 15) return "#8a241f";
    return "#ffffff";
  };

  return (
    <div className="page-grid with-header">
      <div className="contentGrid">
        <div>
          {showPoolTournamentOverModal && (
            <div className="overlay">
              <div className="endGameModal">
                <h3>Pool Tournament Complete</h3>
                <div style={{ fontSize: "12px" }}>
                  {tournamentState?.championUserId
                    ? `Champion user id: ${tournamentState.championUserId}`
                    : "Tournament completed."}
                </div>
                <div className="confirmationButtonsContainer" style={{ display: "flex" }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => dispatch(hideModal("poolTournamentOver"))}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          {showPoolTournamentCancelledModal && (
            <div className="overlay">
              <div className="endGameModal">
                <h3>Pool Tournament Cancelled</h3>
                <div style={{ fontSize: "12px" }}>
                  {tournamentState?.cancelReason || "Tournament was cancelled."}
                </div>
                <div className="confirmationButtonsContainer" style={{ display: "flex" }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => dispatch(hideModal("poolTournamentCancelled"))}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          {isSessionRoute && (
          <div className="pool-tournament-panel in-session">
            <div className="pool-tournament-header">
              <div>
                <div className="pool-title">8 Ball Pool Tournament</div>
                <div className="pool-subtitle">
                  {tournamentState
                    ? `${tournamentState.tournamentId} • ${tournamentState.status} • round ${tournamentState.round}${
                        tournamentState.status === "cancelled" && tournamentState.cancelReason
                          ? ` • ${tournamentState.cancelReason}`
                          : ""
                      }`
                    : "Loading tournament..."}
                </div>
              </div>
              <div className="pool-header-metrics">
                <div className="pool-metric-chip">
                  <span className="pool-metric-label">Players</span>
                  <strong>{participantCount}</strong>
                </div>
                <div className="pool-metric-chip">
                  <span className="pool-metric-label">Starts In</span>
                  <strong>{timeLeft || "--:--:--"}</strong>
                </div>
              </div>
            </div>

            {tournamentState && (
              <div className="pool-bracket-grid">
                <div className="pool-bracket-section">
                  <div className="pool-bracket-title">Players</div>
                  <div className="pool-bracket-list">
                    {tournamentState.participants.map((participant) => (
                      <div key={participant.userId} className="pool-bracket-item">
                        <span>#{participant.seed} {participant.username}</span>
                        <span>{participant.userId === tournamentState.championUserId ? "Champion" : ""}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pool-bracket-section">
                  <div className="pool-bracket-title">Matches</div>
                  {roomState?.rackStates && myRack && opponentRack ? (
                    <div className="pool-rack-row">
                      <div className="pool-rack-card">
                        <div className="pool-rack-title">
                          {myName} • {myRack?.group || "open"}
                        </div>
                        <div className="pool-rack-balls">
                          {(myRack?.pocketedBalls || []).map((number) => (
                            <div
                              key={`session-me-${number}`}
                              className={`pool-mini-ball ${number >= 9 ? "striped" : ""}`}
                              style={{ backgroundColor: poolBallColor(number) }}
                            >
                              <span>{number}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pool-rack-timer">
                          <div className="pool-rack-timer-meta">
                            <span>{currentTurnUserID === user?.id ? "On clock" : "Off clock"}</span>
                            <span>{currentTurnUserID === user?.id ? formatTimer(turnRemainingMs) : "0:00"}</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={currentTurnUserID === user?.id ? turnRemainingPct : 0}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                      <div className="pool-rack-card">
                        <div className="pool-rack-title">
                          {opponentName} • {opponentRack?.group || "open"}
                        </div>
                        <div className="pool-rack-balls">
                          {(opponentRack?.pocketedBalls || []).map((number) => (
                            <div
                              key={`session-opp-${number}`}
                              className={`pool-mini-ball ${number >= 9 ? "striped" : ""}`}
                              style={{ backgroundColor: poolBallColor(number) }}
                            >
                              <span>{number}</span>
                            </div>
                          ))}
                        </div>
                        <div className="pool-rack-timer">
                          <div className="pool-rack-timer-meta">
                            <span>{currentTurnUserID === opponentRack?.userId ? "On clock" : "Off clock"}</span>
                            <span>{currentTurnUserID === opponentRack?.userId ? formatTimer(turnRemainingMs) : "0:00"}</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={currentTurnUserID === opponentRack?.userId ? turnRemainingPct : 0}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="pool-bracket-list">
                      {tournamentState.matches.map((match) => (
                        <div key={match.matchId} className="pool-bracket-item pool-match-item">
                          <div>
                            <strong>R{match.round}</strong>{" "}
                            {participantMap.get(match.playerAUserId) || `#${match.playerAUserId}`} vs{" "}
                            {match.playerBUserId === 0 ? "BYE" : participantMap.get(match.playerBUserId) || `#${match.playerBUserId}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          <PoolGame />
        </div>
      </div>
    </div>
  );
}

export default PoolGameDashboard;
